import React from 'react';
import reactStringReplace from 'react-string-replace';
import { create } from 'zustand';

import {
  type LanguageSource,
  cleanLang,
  getLanguageSource,
  getPreferredLanguage,
  loadDateLocale,
  loadTranslations,
} from '../utils/i18n';

type Translations = Record<string, string>;

interface I18nState {
  lang: string;
  loading: boolean;
  translations: Translations;
  dateLocale: any;
  languageSource: LanguageSource;
  setLang: (lang: string) => Promise<void>;
  load: (lang?: string) => Promise<void>;
  t: (key: string, defaultText?: string) => string;
  translate: (
    key: string,
    replacement?: Record<string, any>,
    options?: { textOnly?: boolean; defaultValue?: string | JSX.Element },
  ) => string | JSX.Element;
}

export const useI18n = create<I18nState>((set, get) => ({
  lang: getPreferredLanguage(),
  loading: true,
  translations: {},
  dateLocale: null,
  languageSource: 'default',
  setLang: async (preferredLanguage: string) => {
    const lang = cleanLang(preferredLanguage);
    try {
      window.localStorage?.setItem('langCode', lang);
    } catch {}
    set({ lang, loading: true, languageSource: getLanguageSource(lang) });
    await get().load(lang);
  },
  load: async (langParam?: string) => {
    const lang = cleanLang(langParam || get().lang || getPreferredLanguage());
    try {
      const [localeMod, translations] = await Promise.all([
        loadDateLocale(lang),
        process.env.NODE_ENV === 'test'
          ? Promise.resolve({})
          : loadTranslations(lang),
      ]);

      const dateLocaleMod = localeMod as any;
      const dateLocale = (dateLocaleMod &&
        (dateLocaleMod.default ?? dateLocaleMod)) as any;
      set({
        lang,
        translations: (translations || {}) as Translations,
        dateLocale,
        loading: false,
        languageSource: getLanguageSource(lang),
      });
    } catch (e) {
      if (lang !== 'en') {
        await get().load('en');
        return;
      }
      set({ loading: false });
    }
  },
  t: (key: string, defaultText?: string) => {
    const { translate } = get();
    return translate(
      key,
      {},
      { textOnly: true, defaultValue: defaultText },
    ) as string;
  },
  translate: (
    translateKey: string,
    replacement: Record<string, any> = {},
    options?: { textOnly?: boolean; defaultValue?: string | JSX.Element },
  ): string | JSX.Element => {
    const { translations, lang } = get();
    const textOnly = options?.textOnly;
    const defaultValue = options?.defaultValue;

    let text: any = translations?.[translateKey] || '';

    if (!text) {
      const msg = `[Translator] Could not translate key: ${translateKey}`;
      if ((window as any).debugTranslations) {
        if (window.location.hostname !== 'www.withlocals.com') {
          console.warn(msg);
        } else {
          console.trace(msg);
        }
      }
      if (defaultValue) {
        text = defaultValue;
      }
    }

    let replacementIndex = 0;
    for (const key in replacement) {
      if (!Object.prototype.hasOwnProperty.call(replacement, key)) continue;
      replacementIndex++;
      const replacementKey = `{${key}}`;
      const indexBase = replacementIndex;
      text = reactStringReplace(text, replacementKey, (match, i) => {
        const replaceResult = replacement[key];
        if (
          typeof replaceResult === 'object' &&
          React.isValidElement(replaceResult)
        ) {
          return React.cloneElement<any>(replaceResult, {
            key: `${indexBase}-${i}-${lang}-${translateKey}`,
          });
        }
        return replaceResult;
      });
    }

    if (textOnly) {
      if (Array.isArray(text)) return text.join('');
      return text;
    }

    return (
      <span key={`${lang}-${translateKey}`} data-translatekey={translateKey}>
        {text}
      </span>
    );
  },
}));

let initialized = false;
export function ensureI18nInitialized() {
  if (!initialized) {
    initialized = true;
    void useI18n.getState().load();
  }
}

ensureI18nInitialized();

// Reactive selectors for frequently used substates
export const useLang = () => useI18n((state) => state.lang);
export const useLangCode = useLang; // alias for clarity/consistency
export const useI18nLoading = () => useI18n((state) => state.loading);
export const useDateLocale = () => useI18n((state) => state.dateLocale);
export const useLanguageSource = () => useI18n((state) => state.languageSource);
export const useSetLang = () => useI18n((state) => state.setLang);
export const useT = () => useI18n((state) => state.t);
export const useTranslate = () => useI18n((state) => state.translate);
